<template>
  <v-menu
    offset-y
    left
    nudge-bottom="22"
    :elevation="$vuetify.theme.dark ? 9 : 8"
    content-class="list-style notification-menu-content"
    max-width="600px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-badge
        :content="unreadCount"
        :value="unreadCount"
        color="error"
        overlap
        offset-x="10"
        offset-y="5"
      >
        <v-icon
          v-bind="attrs"
          v-on="on"
        >
          {{ icons.mdiBellOutline }}
        </v-icon>
      </v-badge>
    </template>
    <v-card class="app-bar-notification-content-container">
      <perfect-scrollbar
        class="ps-user-notifications"
        :options="perfectScrollbarOptions"
      >
        <v-list class="py-0">
          <v-list-item
            class="d-flex"
            link
          >
            <div class="d-flex align-center justify-space-between flex-grow-1">
              <span class="font-weight-semibold">Notifications</span>
              <v-chip
                v-if="unreadCount > 0"
                class="v-chip-light-bg primary--text font-weight-semibold"
                small
              >
                {{ unreadCount }} Nouveau(x)
              </v-chip>
            </div>
          </v-list-item>
          <v-divider></v-divider>

          <template v-for="(notification, index) in notifications">
            <v-list-item
              :key="notification.id"
              link
            >
              <v-list-item-avatar
                class="v-avatar-light-bg primary--text justify-center"
                size="38"
              >
                <v-icon>{{ icons.mdiInformationOutline }}</v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="d-block">
                <v-list-item-title class="text-sm font-weight-semibold">
                  {{ notification.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-sm">
                  {{ notification.body }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <span class="text--secondary text-xs mb-1">{{ formatTime(notification.created_at)
                  }}</span>
                <v-icon
                  v-if="!notification.read_at"
                  color="primary"
                  size="12"
                >
                  {{ icons.mdiCircle }}
                </v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-divider :key="index"></v-divider>
          </template>

          <v-list-item v-if="notifications.length === 0">
            <v-list-item-content>
              <v-list-item-subtitle class="text-center">
                Aucune notification
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="notifications.length > 0"
            key="read-all-btn"
            class="read-all-btn-list-item"
          >
            <v-btn
              block
              color="primary"
              :loading="isLoading"
              :disabled="isLoading || unreadCount === 0"
              @click="markAllAsRead"
            >
              Marquer tout comme lu
            </v-btn>
          </v-list-item>
        </v-list>
      </perfect-scrollbar>
    </v-card>
  </v-menu>
</template>

<script>
// --- Import des dépendances Vue ---
import { ref, onMounted } from '@vue/composition-api'

// --- Import des icônes ---
import {
  mdiBellOutline,
  mdiCircle,
  mdiInformationOutline,
} from '@mdi/js'
import { getInitialName } from '@core/utils'

// --- Import des dépendances 3rd-party ---
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'

// --- Import du client HTTP ---
// Assurez-vous qu'axios est installé (npm install axios)
// et configuré (par ex. avec l'URL de base et les intercepteurs pour le token)
import axios from '@axios'
import store from '@/store'

export default {
  components: {
    PerfectScrollbar,
  },
  setup() {
    // --- États réactifs ---
    const notifications = ref([])
    const unreadCount = ref(0)
    const isLoading = ref(false)

    // --- Options ---
    const perfectScrollbarOptions = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    // --- Fonctions de l'API ---

    /**
     * Récupère la liste paginée des notifications
     */
    const fetchNotifications = async () => {
      try {
        const response = await axios.get('notifications')
        notifications.value = response.data.data // 'data.data' car c'est paginé
      } catch (error) {
        console.error('Erreur lors de la récupération des notifications:', error)
      }
    }

    /**
     * Récupère le nombre de notifications non lues
     */
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get('notifications/unread-count')
        unreadCount.value = response.data.count
      } catch (error) {
        console.error('Erreur lors de la récupération du compteur:', error)
      }
    }

    /**
     * Marque toutes les notifications comme lues
     */
    const markAllAsRead = async () => {
      isLoading.value = true
      try {
        await axios.post('notifications/mark-all-read')

        // Rafraîchir l'état local après succès
        await fetchUnreadCount() // Met le compteur à 0
        await fetchNotifications() // Met à jour le statut (point bleu) de chaque notif
      } catch (error) {
        console.error('Erreur lors du marquage comme lu:', error)
      } finally {
        isLoading.value = false
      }
    }

    // --- Fonctions utilitaires ---

    /**
     * Formate une date ISO en format "time ago" (temps relatif).
     */
    const formatTime = (isoDate) => {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      const now = new Date()
      const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
      if (seconds < 60) {
        return 'maintenant'
      }
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) {
        return `il y a ${minutes} min`
      }
      const hours = Math.floor(minutes / 60)
      if (hours < 24) {
        return `il y a ${hours} heure${hours > 1 ? 's' : ''}`
      }
      const days = Math.floor(hours / 24)
      if (days < 7) {
        return `il y a ${days} jour${days > 1 ? 's' : ''}`
      }
      const weeks = Math.floor(days / 7)
      if (days < 30) {
        return `il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`
      }
      const months = Math.floor(days / 30.44)
      if (months < 12) {
        return `il y a ${months} mois`
      }
      const years = Math.floor(days / 365.25)
      return `il y a ${years} an${years > 1 ? 's' : ''}`
    }

    const onMenuToggle = (isOpen) => {
      // Si le menu s'ouvre, on va chercher la liste fraîche des notifications
      if (isOpen) {
        fetchNotifications()
      }
    }

    // --- Hook de cycle de vie ---
    onMounted(() => {
      if (store.state.app.user.id === store.state.app.settings.gestionnaire_priority_id) {
        // Au chargement du composant, on récupère les données
        fetchNotifications()
        fetchUnreadCount()

        // Optionnel : rafraîchir le compteur toutes les minutes
        // setInterval(fetchUnreadCount, 60000)
        setInterval(fetchUnreadCount, 30000)
        setInterval(fetchNotifications, 30000)
      }
    })

    return {
      // États
      notifications,
      unreadCount,
      isLoading,

      // Méthodes
      markAllAsRead,
      formatTime,
      getInitialName, // Gardé de votre code original
      onMenuToggle,

      // Options
      perfectScrollbarOptions,

      // Icônes
      icons: {
        mdiBellOutline,
        mdiCircle,
        mdiInformationOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.app-bar-notification-content-container {
  .read-all-btn-list-item {
    padding-top: 14px;
    padding-bottom: 14px;
    min-height: unset;
  }
}

.ps-user-notifications {
  max-height: calc(var(--vh, 1vh) * 80);
  min-height: 150px;
}

.notification-menu-content {
  // Vos styles existants
  @media #{map-get($display-breakpoints, 'xs-only')} {
    min-width: calc(100vw - (1.5rem * 2)) !important;
    left: 50% !important;
    transform: translateX(-50%);
  }

  // --- 👇 AJOUTEZ CES RÈGLES ---
  .v-list-item__title,
  .v-list-item__subtitle {
    // Permet au texte de passer à la ligne
    white-space: normal;

    // Permet au composant de s'adapter à la hauteur du texte
    height: auto;

    // Assure que le texte n'est pas caché
    overflow: visible;

    // (Optionnel) Améliore la lisibilité des lignes multiples
    line-height: 1.3;
  }

  // --- 👆 FIN DES AJOUTS ---
}
</style>
